import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button.jsx'
import Icon from '../../../components/ui/Icon.jsx'
import { useFeedback } from '../../../components/ui/feedback.jsx'
import api from '../../../lib/api.js'

/*
 * Step 5 — Peppol registration (required). POST /companies/:id/peppol/register
 * (auto-detects params). The registration starts AUTOMATICALLY on arrival —
 * checking → registering (animated) → success / error (with retry). No click.
 */
export default function StepPeppol({ companyId, bindSubmit }) {
  const { t } = useTranslation()
  const { toast } = useFeedback()
  const [phase, setPhase] = useState('checking') // checking | registering | success | error
  const [message, setMessage] = useState('')
  const started = useRef(false)

  const register = async () => {
    setPhase('registering'); setMessage('')
    try {
      const { data } = await api.post(`/companies/${companyId}/peppol/register`)
      setPhase('success'); setMessage(data?.message || '')
    } catch (e) {
      const err = e?.response?.data?.error || ''
      // "Already registered" (PMC06) counts as success.
      if (/déjà|already|PMC06/i.test(err)) { setPhase('success'); setMessage(err) }
      else { setPhase('error'); setMessage(err || t('setup.steps.peppol.failed')) }
    }
  }

  // On arrival: if already registered show success, otherwise auto-start.
  useEffect(() => {
    if (started.current) return
    started.current = true
    api.get(`/companies/${companyId}`)
      .then(({ data }) => {
        if ((data?.company || data)?.peppol_registered) { setPhase('success'); return }
        register()
      })
      .catch(() => register())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  useEffect(() => {
    // Block "Suivant" while still working; allow once done (success) or failed.
    bindSubmit(async () => {
      if (phase === 'success' || phase === 'error') return true
      toast(t('setup.steps.peppol.wait'), 'info')
      return false
    })
  }, [phase, bindSubmit, t, toast])

  return (
    <>
      <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 18 }}>{t('setup.steps.peppol.intro')}</p>

      {(phase === 'checking' || phase === 'registering') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 18, background: 'var(--color-bg-lilac)', borderRadius: 12 }}>
          <span className="vspinner" style={{ width: 26, height: 26 }} />
          <div>
            <div style={{ fontWeight: 700, color: 'var(--color-ink)' }}>{t('setup.steps.peppol.registering')}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('setup.steps.peppol.registeringHint')}</div>
          </div>
        </div>
      )}

      {phase === 'success' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: 'var(--color-success-bg)', color: 'var(--color-success)', borderRadius: 12, animation: 'vfade .25s ease' }}>
          <Icon name="check" size={22} /><div><div style={{ fontWeight: 700 }}>{t('setup.steps.peppol.registered')}</div>{message && <div style={{ fontSize: 13 }}>{message}</div>}</div>
        </div>
      )}

      {phase === 'error' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 12, marginBottom: 14 }}>
            <Icon name="alert-triangle" size={22} /><div>{message}</div>
          </div>
          <Button variant="secondary" icon="link" onClick={register}>{t('setup.steps.peppol.retry')}</Button>
          <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 10 }}>{t('setup.steps.peppol.canSkip')}</p>
        </>
      )}
    </>
  )
}
